import React, { useState } from 'react';
import type { ReactNode } from 'react';

interface CollapseProps {
  children: ReactNode;
  bordered?: boolean;
  accordion?: boolean;
  defaultActiveKey?: string | string[];
  activeKey?: string | string[];
  expandIcon?: (props: { isActive: boolean; className?: string }) => ReactNode;
  expandIconPosition?: 'left' | 'right';
  onChange?: (key: string | string[]) => void;
  className?: string;
}

interface PanelProps {
  children: ReactNode;
  header: ReactNode;
  panelKey: string;
  className?: string;
  showArrow?: boolean;
  collapsible?: 'disabled' | 'header' | 'icon';
  forceRender?: boolean;
  isActive?: boolean;
  onToggle?: (key: string) => void;
  expandIcon?: (props: { isActive: boolean; className?: string }) => ReactNode;
  expandIconPosition?: 'left' | 'right';
}

export function Collapse({
  children,
  bordered = true,
  accordion = false,
  defaultActiveKey,
  activeKey,
  expandIcon,
  expandIconPosition = 'right',
  onChange,
  className = '',
}: CollapseProps) {
  const [internalActiveKeys, setInternalActiveKeys] = useState<string[]>(() => {
    if (activeKey !== undefined) {
      return Array.isArray(activeKey) ? activeKey : [activeKey];
    }
    if (defaultActiveKey !== undefined) {
      return Array.isArray(defaultActiveKey) ? defaultActiveKey : [defaultActiveKey];
    }
    return [];
  });

  const currentActiveKeys =
    activeKey !== undefined ?
      Array.isArray(activeKey) ?
        activeKey
      : [activeKey]
    : internalActiveKeys;

  const handlePanelToggle = (key: string) => {
    let newActiveKeys: string[];

    if (accordion) {
      // In accordion mode, if the clicked panel is already active, close it
      // Otherwise, close all other panels and open only this one
      newActiveKeys = currentActiveKeys.includes(key) ? [] : [key];
    } else {
      // In non-accordion mode, toggle the clicked panel
      newActiveKeys = currentActiveKeys.includes(key) ? currentActiveKeys.filter((k) => k !== key) : [...currentActiveKeys, key];
    }

    if (activeKey === undefined) {
      setInternalActiveKeys(newActiveKeys);
    }

    onChange?.(accordion ? newActiveKeys[0] || '' : newActiveKeys);
  };

  // Clone children and pass props to each Panel
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Panel) {
      const panelProps: Partial<PanelProps> = {
        isActive: currentActiveKeys.includes(child.props.panelKey),
        onToggle: handlePanelToggle,
        expandIconPosition,
      };

      // Solo incluir expandIcon si está definido
      if (expandIcon !== undefined) {
        panelProps.expandIcon = expandIcon;
      }

      return React.cloneElement(child as React.ReactElement<PanelProps>, panelProps);
    }
    return child;
  });

  // Use Tailwind classes but allow custom className to override
  const baseClasses = 'bg-white';
  const borderClasses = bordered ? 'border border-gray-300 rounded-md' : 'border-none bg-transparent';

  return <div className={`custom-collapse ${baseClasses} ${borderClasses} ${className}`}>{childrenWithProps}</div>;
}

export function Panel({
  children,
  header,
  panelKey,
  className = '',
  showArrow = true,
  collapsible,
  forceRender = false,
  isActive = false,
  onToggle,
  expandIcon,
  expandIconPosition = 'right',
}: PanelProps) {
  const isDisabled = collapsible === 'disabled';

  const handleHeaderClick = () => {
    if (!isDisabled && onToggle) {
      onToggle(panelKey);
    }
  };

  const renderExpandIcon = () => {
    if (!showArrow) return null;

    if (expandIcon) {
      return expandIcon({ isActive, className: 'transition-transform duration-300' });
    }

    return (
      <span
        className={`expand-icon flex items-center justify-center transition-transform duration-300 ease-in-out ${isActive ? 'rotate-180' : 'rotate-0'} ${expandIconPosition === 'left' ? 'mr-2' : 'ml-2'}`}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-gray-600">
          <path d="M6 8.5L2 4.5L2.7 3.8L6 7.1L9.3 3.8L10 4.5L6 8.5Z" fill="currentColor" />
        </svg>
      </span>
    );
  };

  const shouldRenderContent = isActive || forceRender;

  return (
    <div className={`collapse-panel border-gray-300 border-b ${isDisabled ? 'opacity-50' : ''} ${className}`}>
      <div
        className={`collapse-header flex cursor-pointer select-none items-center bg-transparent px-0 py-3 capitalize transition-colors duration-300 ${isDisabled ? 'cursor-not-allowed' : ''} ${expandIconPosition === 'left' ? 'flex-row' : 'flex-row'}`}
        onClick={handleHeaderClick}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleHeaderClick();
          }
        }}
      >
        {expandIconPosition === 'left' && renderExpandIcon()}
        <div className="header-content flex-1 text-sm">{header}</div>
        {expandIconPosition === 'right' && renderExpandIcon()}
      </div>
      <div
        className={`collapse-content overflow-hidden transition-all duration-300 ease-in-out ${isActive ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="content-inner bg-transparent py-0 pb-4 pt-1">{children}</div>
      </div>
    </div>
  );
}

// Attach Panel to Collapse for convenience
Collapse.Panel = Panel;
